/**
 * ForgotPasswordPage — Part A of the password-reset flow.
 *
 * Simple centered card. Single email input + submit button.
 * On any 200 (which includes rate-limited + nonexistent-account +
 * OAuth-only + happy paths — server returns the same neutral
 * message in all cases) the form flips to a permanent success
 * state showing the neutral instruction. The submit button also
 * self-disables for 30 seconds after any submit to prevent
 * hammering.
 *
 * NO error surfacing beyond basic client-side email format
 * validation — the server intentionally does not distinguish
 * "not-found" / "rate-limited" / "sent" states to prevent
 * account-enumeration attacks. Frontend mirrors that posture.
 *
 * Route: `/forgot-password` (public, unauthenticated).
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import PublicPageShell from "@/components/PublicPageShell";
import { Loader2 } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REQUEST_COOLDOWN_MS = 30 * 1000;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Cooldown countdown so the user sees WHY the button is disabled.
  const [cooldownSecs, setCooldownSecs] = useState(0);

  const emailValid = EMAIL_RE.test(email);

  const startCooldown = () => {
    setCooldownSecs(Math.floor(REQUEST_COOLDOWN_MS / 1000));
    const started = Date.now();
    const tick = () => {
      const elapsed = Date.now() - started;
      const remaining = Math.max(0, Math.ceil((REQUEST_COOLDOWN_MS - elapsed) / 1000));
      setCooldownSecs(remaining);
      if (remaining > 0) setTimeout(tick, 1000);
    };
    setTimeout(tick, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!emailValid) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      // Neutral success — always, regardless of outcome. Backend
      // returns the same body for existing / nonexistent / rate-limited
      // / OAuth-only accounts; frontend must not distinguish either.
      setSubmitted(true);
      startCooldown();
    } catch (err) {
      // Only reachable on 4xx/5xx OTHER than the neutral 200. Malformed
      // email hits 422; network failure hits a rejection.  We surface
      // a soft error but do NOT try to re-classify the response body
      // (which would leak account-existence signal on some code paths).
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = emailValid && !submitting && cooldownSecs === 0;

  return (
    <PublicPageShell mainClass="flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <Link to="/" data-testid="forgot-password-logo-link" className="block">
            <Logo variant="full" className="h-32 w-32" />
          </Link>
          <h1 className="mt-2 font-heading text-3xl font-bold text-pp-text">
            Forgot password?
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            No worries — we'll send you a link to set a new one.
          </p>
        </div>

        <div
          className="rounded-3xl border border-border bg-pp-surface p-8 shadow-sm"
          data-testid="forgot-password-card"
        >
          {submitted ? (
            <div className="space-y-4" data-testid="forgot-password-success">
              <div className="rounded-2xl bg-pp-surface-2 p-6 text-center">
                <p className="text-2xl" aria-hidden="true">🐾</p>
                <p className="mt-3 text-base font-semibold text-pp-text">
                  Check your inbox
                </p>
                <p
                  className="mt-2 text-sm text-muted-foreground"
                  data-testid="forgot-password-neutral-message"
                >
                  If an account exists for that email, we've sent reset instructions.
                </p>
                <p className="mt-3 text-xs text-muted-foreground">
                  Didn't get an email? Check spam, or try again{" "}
                  {cooldownSecs > 0
                    ? `in ${cooldownSecs}s.`
                    : "in a bit."}
                </p>
              </div>
              <p className="text-center text-sm text-muted-foreground">
                Remembered it?{" "}
                <Link
                  to="/login"
                  className="font-semibold text-brand-orange hover:underline"
                  data-testid="forgot-password-back-to-login"
                >
                  Back to log in
                </Link>
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="forgot-password-form"
            >
              <div>
                <Label htmlFor="fp-email" className="text-sm font-semibold text-pp-text">
                  Email
                </Label>
                <Input
                  id="fp-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-2 h-12 rounded-full border-border bg-pp-surface px-5"
                  required
                  autoFocus
                  data-testid="forgot-password-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={!canSubmit}
                className="h-12 w-full rounded-full bg-pp-purple text-base text-white hover:bg-pp-purple-dark"
                data-testid="forgot-password-submit-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Send reset link"
                )}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Remembered it?{" "}
                <Link
                  to="/login"
                  className="font-semibold text-brand-orange hover:underline"
                  data-testid="forgot-password-back-to-login"
                >
                  Back to log in
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </PublicPageShell>
  );
}
